'use client'

import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { CookiePreferencesLink } from '@/components/CookiePreferencesLink'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { EN_FOOTER_LINKS, type FooterLinks } from '@/components/footerLinks'

/**
 * Presentational footer, safe in every bundle (client, edge, server) — no fs.
 * Hrefs default to the English URLs; the server `Footer` wrapper injects
 * locale-resolved links on localized pages. English-only surfaces (the edge
 * calendar view, the shared-link client pages) render <FooterView /> directly.
 */
export function FooterView({ links = EN_FOOTER_LINKS }: { links?: FooterLinks }) {
  const t = useTranslations('footer')
  const tc = useTranslations('common')

  return (
    <footer className="px-[24px] py-[64px] flex flex-col gap-[32px]">
      {/* Link sections. Norwegian/German compound words (Måltidsplanlegging,
          Inspirasjonsbiblioteket) overflow the fixed columns: hyphens-auto +
          break-words (both inherit) wrap them, min-w-0 lets grid items shrink. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-[24px] break-words hyphens-auto">
        {/* About Homie */}
        <div className="min-w-0 flex flex-col gap-[12px]">
          <h3 className="text-s uppercase tracking-[0.1em] text-[#666666]">{t('aboutHomie')}</h3>
          <nav className="flex flex-col gap-[8px]">
            <Link href={links.anchorWhyHomie} className="text-l">{t('aboutWhyHomie')}</Link>
            <Link href={links.anchorFeatures} className="text-l">{t('aboutFeatures')}</Link>
            <Link href={links.anchorPricing} className="text-l">{t('aboutPricing')}</Link>
            <Link href={links.forOrganisations} className="text-l">{t('aboutForOrganisations')}</Link>
            <Link href={links.about} className="text-l">{t('aboutTeamStory')}</Link>
          </nav>
        </div>

        {/* Features */}
        <div className="min-w-0 flex flex-col gap-[12px]">
          <h3 className="text-s uppercase tracking-[0.1em] text-[#666666]">{t('sectionFeatures')}</h3>
          <nav className="flex flex-col gap-[8px]">
            <Link href={links.featureSharedCalendar} className="text-l">{t('featuresSharedCalendar')}</Link>
            <Link href={links.featureFamilyTasks} className="text-l">{t('featuresFamilyTasks')}</Link>
            <Link href={links.featureRoutines} className="text-l">{t('featuresRoutines')}</Link>
            <Link href={links.featureMealPlanning} className="text-l">{t('featuresMealPlanning')}</Link>
            <Link href={links.featureNotes} className="text-l">{t('featuresNotes')}</Link>
            <Link href={links.featureInspiration} className="text-l">{t('featuresInspiration')}</Link>
            <Link href={links.featuresAll} className="text-l">{t('featuresAll')}</Link>
          </nav>
        </div>

        {/* Use Cases */}
        <div className="min-w-0 flex flex-col gap-[12px]">
          <h3 className="text-s uppercase tracking-[0.1em] text-[#666666]">{t('homieFor')}</h3>
          <nav className="flex flex-col gap-[8px]">
            <Link href={links.forBusyParents} className="text-l">{t('forBusyParents')}</Link>
            <Link href={links.forCouples} className="text-l">{t('forCouples')}</Link>
            <Link href={links.forAdhdFamilies} className="text-l">{t('forAdhdFamilies')}</Link>
            <Link href={links.forCoParenting} className="text-l">{t('forCoParents')}</Link>
            <Link href={links.forLargeFamilies} className="text-l">{t('forLargeFamilies')}</Link>
            <Link href={links.forHomeschool} className="text-l">{t('forHomeschoolers')}</Link>
          </nav>
        </div>

        {/* Inspiration / Content */}
        <div className="min-w-0 flex flex-col gap-[12px]">
          <h3 className="text-s uppercase tracking-[0.1em] text-[#666666]">{t('learn')}</h3>
          <nav className="flex flex-col gap-[8px]">
            <Link href={links.postWritingThingsDown} className="text-l">{t('learnWritingThingsDown')}</Link>
            <Link href={links.postCalmTech} className="text-l">{t('learnCalmTech')}</Link>
            <Link href={links.blogAll} className="text-l">{t('learnAllArticles')}</Link>
            <Link href={links.inspirationLibrary} className="text-l">{t('learnInspirationLibrary')}</Link>
            <Link href={links.learn} className="text-l">{t('learnVideos')}</Link>
          </nav>
        </div>
      </div>

      {/* Compare - full width section */}
      <div className="flex flex-col gap-[12px]">
        <h3 className="text-s uppercase tracking-[0.1em] text-[#666666]">{t('compare')}</h3>
        <nav className="flex flex-col gap-[8px]">
          <Link href={links.compareAppleCalendar} className="text-l">{t('compareAppleCalendar')}</Link>
          <Link href={links.compareCalendarsReaddle} className="text-l">{t('compareCalendarsReaddle')}</Link>
          <Link href={links.compareCozi} className="text-l">{t('compareCozi')}</Link>
          <Link href={links.compareFamcal} className="text-l">{t('compareFamcal')}</Link>
          <Link href={links.compareFamilywall} className="text-l">{t('compareFamilyWall')}</Link>
          <Link href={links.compareGoogleCalendar} className="text-l">{t('compareGoogleCalendar')}</Link>
          <Link href={links.compareMaple} className="text-l">{t('compareMaple')}</Link>
          <Link href={links.compareSortifyd} className="text-l">{t('compareSortifyd')}</Link>
          <Link href={links.compareTimetree} className="text-l">{t('compareTimeTree')}</Link>
        </nav>
      </div>

      {/* Legal / Support - horizontal row */}
      <nav className="flex flex-wrap gap-x-[24px] gap-y-[8px] text-l text-[#666666]">
        <Link href={links.about}>{tc('about')}</Link>
        <Link href={links.legalPrivacy}>{tc('legalPrivacy')}</Link>
        <Link href={links.legalTerms}>{tc('legalTerms')}</Link>
        <Link href={links.legalSupport}>{tc('legalSupport')}</Link>
        <CookiePreferencesLink className="text-left text-l">{tc('legalCookies')}</CookiePreferencesLink>
        <LanguageSwitcher />
      </nav>
    </footer>
  )
}
